import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  emptyState?: React.ReactNode;
  onRowClick?: (row: TData) => void;
  rowClassName?: (row: TData) => string | undefined;
  initialSorting?: SortingState;
}

/**
 * Thin wrapper around `@tanstack/react-table` that plugs into the
 * shadcn-styled `Table` primitives. Keeps the screen code declarative:
 * pass `columns` + `data`, get a fully styled, sortable, optionally
 * clickable table back. Cursor-pagination is the caller's job — this
 * is purely a render layer.
 */
export function DataTable<TData>({
  columns,
  data,
  emptyState,
  onRowClick,
  rowClassName,
  initialSorting,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting ?? []);

  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (data.length === 0 && emptyState) {
    return <div className="rounded-xl border border-line bg-card">{emptyState}</div>;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-card">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-secondary/40 hover:bg-secondary/40">
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  style={{ width: header.getSize() === 0 ? undefined : header.getSize() }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => {
            const data = row.original;
            const onClick = onRowClick ? () => onRowClick(data) : undefined;
            return (
              <TableRow
                key={row.id}
                onClick={onClick}
                className={cn(
                  onClick ? 'cursor-pointer' : undefined,
                  rowClassName?.(data),
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
